import { useState, useEffect, useCallback, memo } from 'react';
import { DayPicker } from 'react-day-picker';
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  cn,
} from '@strivelabs/ui-core';
import { Calendar, ChevronLeft, ChevronRight, Loader2, RotateCcw } from 'lucide-react';
import { format, subDays, differenceInDays, parse } from 'date-fns';
import 'react-day-picker/style.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface DateRange {
  /** ISO date string YYYY-MM-DD */
  startDate: string;
  /** ISO date string YYYY-MM-DD */
  endDate: string;
}

export interface DateRangePresetPickerValue {
  /** The primary (current) period selected by the user. */
  currentPeriod: DateRange;
  /** The comparison (baseline) period — auto-calculated as the same duration immediately before currentPeriod. */
  baselinePeriod: DateRange;
}

export interface DateRangePreset {
  /** Unique key for this preset. */
  key: string;
  /** Display label (e.g. "Last 7 days"). */
  label: string;
  /** Number of days the preset covers. */
  days: number;
}

export interface DateRangePresetPickerProps {
  /** Controlled value — `null` means "no selection / default data". */
  value: DateRangePresetPickerValue | null;
  /** Called when the user applies a selection (preset or custom range). */
  onChange: (state: DateRangePresetPickerValue | null) => void;
  /** Show a loading spinner on the trigger button. */
  isLoading?: boolean;
  /** Available presets. Defaults to Last 7 / 14 / 30 days. */
  presets?: DateRangePreset[];
  /** Number of calendar months to display. @default 2 */
  numberOfMonths?: number;
  /** Popover alignment relative to the trigger. @default "end" */
  align?: 'start' | 'center' | 'end';
  /** Additional class names for the trigger button. */
  className?: string;
  /** Label shown when no value is selected. @default "Compare Periods" */
  placeholder?: string;
}

/* ------------------------------------------------------------------ */
/*  Defaults                                                           */
/* ------------------------------------------------------------------ */

const DEFAULT_PRESETS: DateRangePreset[] = [
  { key: 'last_7', label: 'Last 7 days', days: 7 },
  { key: 'last_14', label: 'Last 14 days', days: 14 },
  { key: 'last_30', label: 'Last 30 days', days: 30 },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function toDate(s: string): Date {
  return parse(s, 'yyyy-MM-dd', new Date());
}

function computePeriodsFromDays(days: number): DateRangePresetPickerValue {
  const today = new Date();
  const currentEnd = today;
  const currentStart = subDays(today, days - 1);
  const baselineEnd = subDays(currentStart, 1);
  const baselineStart = subDays(baselineEnd, days - 1);

  return {
    currentPeriod: {
      startDate: format(currentStart, 'yyyy-MM-dd'),
      endDate: format(currentEnd, 'yyyy-MM-dd'),
    },
    baselinePeriod: {
      startDate: format(baselineStart, 'yyyy-MM-dd'),
      endDate: format(baselineEnd, 'yyyy-MM-dd'),
    },
  };
}

function computeBaselineFromRange(
  from: Date,
  to: Date,
): { baselineStart: Date; baselineEnd: Date } {
  const days = differenceInDays(to, from) + 1;
  const baselineEnd = subDays(from, 1);
  const baselineStart = subDays(baselineEnd, days - 1);
  return { baselineStart, baselineEnd };
}

function buildValueFromDates(from: Date, to: Date): DateRangePresetPickerValue {
  const { baselineStart, baselineEnd } = computeBaselineFromRange(from, to);
  return {
    currentPeriod: {
      startDate: format(from, 'yyyy-MM-dd'),
      endDate: format(to, 'yyyy-MM-dd'),
    },
    baselinePeriod: {
      startDate: format(baselineStart, 'yyyy-MM-dd'),
      endDate: format(baselineEnd, 'yyyy-MM-dd'),
    },
  };
}

/** Memoized baseline date summary to avoid recomputing on every render. */
const BaselineSummary = memo(function BaselineSummary({ from, to }: { from: Date; to: Date }) {
  const { baselineStart, baselineEnd } = computeBaselineFromRange(from, to);
  return (
    <span>
      {format(baselineStart, 'MMM d')} – {format(baselineEnd, 'MMM d, yyyy')}
    </span>
  );
});

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function DateRangePresetPicker({
  value,
  onChange,
  isLoading = false,
  presets = DEFAULT_PRESETS,
  numberOfMonths = 2,
  align = 'end',
  className,
  placeholder = 'Compare Periods',
}: DateRangePresetPickerProps) {
  const [open, setOpen] = useState(false);

  // Internal draft state — updated by presets/calendar; only committed on "Update".
  const [draftFrom, setDraftFrom] = useState<Date | undefined>(undefined);
  const [draftTo, setDraftTo] = useState<Date | undefined>(undefined);
  const [draftPresetKey, setDraftPresetKey] = useState<string | null>(null);

  // The committed preset key (shown on the trigger after apply)
  const [committedPresetKey, setCommittedPresetKey] = useState<string | null>(null);

  // Sync draft state when external value changes (e.g. reset from parent)
  useEffect(() => {
    if (value) {
      setDraftFrom(toDate(value.currentPeriod.startDate));
      setDraftTo(toDate(value.currentPeriod.endDate));
    } else {
      setDraftFrom(undefined);
      setDraftTo(undefined);
      setDraftPresetKey(null);
      setCommittedPresetKey(null);
    }
  }, [value]);

  // Reset draft to committed state when popover opens
  useEffect(() => {
    if (open) {
      if (value) {
        setDraftFrom(toDate(value.currentPeriod.startDate));
        setDraftTo(toDate(value.currentPeriod.endDate));
        setDraftPresetKey(committedPresetKey);
      } else {
        setDraftFrom(undefined);
        setDraftTo(undefined);
        setDraftPresetKey(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ---------- Handlers ---------- */

  const handlePresetClick = useCallback(
    (preset: DateRangePreset) => {
      const periods = computePeriodsFromDays(preset.days);
      setDraftFrom(toDate(periods.currentPeriod.startDate));
      setDraftTo(toDate(periods.currentPeriod.endDate));
      setDraftPresetKey(preset.key);
    },
    [],
  );

  const handleApply = useCallback(() => {
    if (!draftFrom || !draftTo) return;
    const result = buildValueFromDates(draftFrom, draftTo);
    setCommittedPresetKey(draftPresetKey);
    onChange(result);
    setOpen(false);
  }, [draftFrom, draftTo, draftPresetKey, onChange]);

  const handleReset = useCallback(() => {
    setDraftFrom(undefined);
    setDraftTo(undefined);
    setDraftPresetKey(null);
    setCommittedPresetKey(null);
    onChange(null);
    setOpen(false);
  }, [onChange]);

  /* ---------- Trigger label ---------- */

  const triggerLabel = (() => {
    if (!value) return placeholder;
    if (committedPresetKey) {
      const preset = presets.find((p) => p.key === committedPresetKey);
      if (preset) return preset.label;
    }
    const from = toDate(value.currentPeriod.startDate);
    const to = toDate(value.currentPeriod.endDate);
    return `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
  })();

  /* ---------- Render ---------- */

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-8 rounded-full text-xs gap-2 font-medium px-3.5',
            value
              ? 'border-foreground/20 bg-foreground/5 text-foreground'
              : 'text-muted-foreground',
            className,
          )}
        >
          <Calendar className="h-3.5 w-3.5 shrink-0" />
          {triggerLabel}
          {isLoading && <Loader2 className="h-3 w-3 animate-spin shrink-0" />}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align={align}
        className="w-auto p-0 flex flex-row rounded-xl"
        sideOffset={8}
      >
        {/* Calendar area */}
        <div className="p-4 border-r border-border">
          <DayPicker
            mode="range"
            numberOfMonths={numberOfMonths}
            selected={
              draftFrom && draftTo
                ? { from: draftFrom, to: draftTo }
                : undefined
            }
            onSelect={(range) => {
              setDraftFrom(range?.from ?? undefined);
              setDraftTo(range?.to ?? undefined);
              // Clear preset when manually picking dates
              if (range?.from || range?.to) {
                setDraftPresetKey(null);
              }
            }}
            disabled={{ after: new Date() }}
            components={{
              Chevron: ({ orientation }) =>
                orientation === 'left' ? (
                  <ChevronLeft className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                ),
            }}
            classNames={{
              root: 'text-sm',
              months: 'flex gap-6',
              month_caption:
                'flex justify-center items-center h-8 text-sm font-semibold',
              nav: 'flex items-center gap-1',
              button_previous:
                'absolute left-1 top-0.5 h-7 w-7 rounded-full bg-transparent p-0 text-muted-foreground hover:bg-accent hover:text-foreground inline-flex items-center justify-center transition-colors',
              button_next:
                'absolute right-1 top-0.5 h-7 w-7 rounded-full bg-transparent p-0 text-muted-foreground hover:bg-accent hover:text-foreground inline-flex items-center justify-center transition-colors',
              weekdays: 'flex',
              weekday: 'text-muted-foreground w-9 text-xs font-medium',
              week: 'flex mt-1',
              day: 'h-9 w-9 p-0 text-center text-sm relative',
              day_button:
                'h-9 w-9 rounded-full inline-flex items-center justify-center transition-colors cursor-pointer font-normal hover:bg-accent hover:text-accent-foreground',
              selected:
                '[&>button]:bg-foreground [&>button]:text-background [&>button]:hover:bg-foreground [&>button]:hover:text-background [&>button]:font-medium',
              range_start: 'bg-accent rounded-l-full',
              range_end: 'bg-accent rounded-r-full',
              range_middle:
                'bg-accent rounded-none [&>button]:bg-transparent [&>button]:text-foreground [&>button]:hover:bg-foreground/10',
              today: '[&>button]:font-bold [&>button]:underline underline-offset-4',
              disabled: 'text-muted-foreground/40 pointer-events-none',
              outside: 'text-muted-foreground/40',
            }}
          />

          {/* Summary + apply row */}
          <div className="flex items-center justify-between border-t border-border pt-3 mt-2 px-1">
            <div className="text-xs text-muted-foreground">
              {draftFrom && draftTo ? (
                <>
                  <span className="font-medium text-foreground">
                    {format(draftFrom, 'MMM d')} –{' '}
                    {format(draftTo, 'MMM d, yyyy')}
                  </span>
                  <span className="mx-1.5 text-muted-foreground">vs</span>
                  <BaselineSummary from={draftFrom} to={draftTo} />
                </>
              ) : (
                <span>Select a date range</span>
              )}
            </div>
            <div className="flex gap-2">
              {value && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleReset}
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                size="sm"
                variant="black"
                className="h-7 text-xs rounded-md bg-foreground text-background hover:bg-foreground/90"
                onClick={handleApply}
                disabled={!draftFrom || !draftTo}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>

        {/* Presets sidebar */}
        <div className="flex flex-col py-4 px-2 min-w-[130px]">
          {presets.map((preset) => (
            <button
              key={preset.key}
              onClick={() => handlePresetClick(preset)}
              className={cn(
                'text-sm text-left px-3 py-2 rounded-lg transition-colors whitespace-nowrap',
                draftPresetKey === preset.key
                  ? 'bg-foreground/10 text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
